/**
 * Authentication Store (Supabase)
 * 
 * Handles user authentication, session management, and profile data.
 * Uses Supabase Auth for secure authentication with OAuth support.
 * 
 * Features:
 * - Email/password authentication
 * - OAuth (Google)
 * - Session persistence
 * - Auto-refresh tokens
 * - Profile management
 * - Password reset
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { supabase } from '@/lib/supabase';
import type { User, Session, AuthError, AuthChangeEvent, PostgrestSingleResponse } from '@supabase/supabase-js';
import type { Profile } from '@/lib/supabase';
import { useToastStore } from '@/stores/toast';
import { structuredLogger } from '@/services/logger';
import { createRecoveryController } from '@/stores/modules/auth/recoveryController';
import { buildCallbackUrl } from '@/stores/modules/auth/utils';
import { analyticsService } from '@/services/analyticsService';

export const DEFAULT_TIMEOUT_MS = 15000;
export const PROFILE_QUERY_TIMEOUT_MS = 10000;
export const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

export type RetryOptions = {
  maxAttempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
};

const DEFAULT_RETRY_OPTIONS: Required<Omit<RetryOptions, 'shouldRetry'>> = {
  maxAttempts: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000
};

const defaultShouldRetry = (err: unknown): boolean => {
  if (err instanceof Error) {
    const message = err.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('timeout') ||
      message.includes('connection')
    );
  }
  return false;
};

export async function withTimeout<T>(
  operation: () => Promise<T>,
  timeoutMs: number,
  operationName: string
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      const timeoutError = new Error(`${operationName} timed out after ${timeoutMs}ms`);
      structuredLogger.warn('[auth] timeout', {
        operationName,
        timeoutMs
      });
      analyticsService.track('auth_timeout', {
        operationName,
        timeoutMs
      });
      reject(timeoutError);
    }, timeoutMs);
  });

  try {
    return await Promise.race([operation(), timeoutPromise]) as T;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions
): Promise<T> {
  const resolvedOptions = {
    maxAttempts: options.maxAttempts ?? DEFAULT_RETRY_OPTIONS.maxAttempts,
    baseDelayMs: options.baseDelayMs ?? DEFAULT_RETRY_OPTIONS.baseDelayMs,
    maxDelayMs: options.maxDelayMs ?? DEFAULT_RETRY_OPTIONS.maxDelayMs,
    shouldRetry: options.shouldRetry ?? defaultShouldRetry
  };

  let lastError: unknown;

  for (let attempt = 1; attempt <= resolvedOptions.maxAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (err) {
      lastError = err;

      if (attempt === resolvedOptions.maxAttempts || !resolvedOptions.shouldRetry(err, attempt)) {
        structuredLogger.error('[auth] retry:exhausted', {
          attempt,
          maxAttempts: resolvedOptions.maxAttempts,
          error: err instanceof Error ? err.message : 'unknown'
        });
        throw err;
      }

      const delay = Math.min(
        resolvedOptions.baseDelayMs * Math.pow(2, attempt - 1),
        resolvedOptions.maxDelayMs
      );

      structuredLogger.info('[auth] retry', {
        attempt,
        maxAttempts: resolvedOptions.maxAttempts,
        delayMs: delay,
        error: err instanceof Error ? err.message : 'unknown'
      });
      analyticsService.track('auth_retry_scheduled', {
        attempt,
        maxAttempts: resolvedOptions.maxAttempts,
        delayMs: delay
      });

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

export enum AuthErrorCategory {
  Offline = 'offline',
  Timeout = 'timeout',
  Network = 'network',
  Credentials = 'credentials',
  Recovery = 'recovery',
  RateLimit = 'rate_limit',
  Validation = 'validation',
  Unknown = 'unknown'
}

const AUTH_ERROR_COPY: Record<AuthErrorCategory, string> = {
  [AuthErrorCategory.Offline]: 'No internet connection detected. Reconnect and try again.',
  [AuthErrorCategory.Timeout]: 'This request took too long. Please try again in a moment.',
  [AuthErrorCategory.Network]: 'We could not reach the server. Check your connection.',
  [AuthErrorCategory.Credentials]: 'Those credentials did not work. Please recheck and try again.',
  [AuthErrorCategory.Recovery]: 'This recovery link is invalid or expired. Request a new email.',
  [AuthErrorCategory.RateLimit]: 'Too many attempts. Please wait a few seconds and retry.',
  [AuthErrorCategory.Validation]: 'Please review the highlighted fields and try again.',
  [AuthErrorCategory.Unknown]: 'Something went wrong. Please try again.'
};

const getUserFacingMessage = (category: AuthErrorCategory, fallback: string): string => {
  return AUTH_ERROR_COPY[category] ?? fallback;
};

const categorizeAuthError = (err: unknown, fallback: AuthErrorCategory): AuthErrorCategory => {
  if (err && typeof err === 'object') {
    const authErr = err as AuthError & { status?: number; code?: string };
    if (typeof authErr.status === 'number') {
      if (authErr.status === 408) {
        return AuthErrorCategory.Timeout;
      }
      if (authErr.status === 401 || authErr.status === 403) {
        return AuthErrorCategory.Credentials;
      }
      if (authErr.status === 429) {
        return AuthErrorCategory.RateLimit;
      }
      if (authErr.status >= 500) {
        return AuthErrorCategory.Network;
      }
    }
    if (typeof authErr.code === 'string') {
      const code = authErr.code.toLowerCase();
      if (code.includes('otp') || code.includes('recovery')) {
        return AuthErrorCategory.Recovery;
      }
      if (code.includes('timeout')) {
        return AuthErrorCategory.Timeout;
      }
    }
  }

  if (err instanceof Error) {
    const message = err.message.toLowerCase();
    if (message.includes('timeout')) {
      return AuthErrorCategory.Timeout;
    }
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return AuthErrorCategory.Network;
    }
    if (message.includes('password') || message.includes('credentials')) {
      return AuthErrorCategory.Credentials;
    }
    if (message.includes('recovery') || message.includes('expired')) {
      return AuthErrorCategory.Recovery;
    }
  }

  return fallback;
};

export type SupabasePromiseResult<T extends (...args: any[]) => Promise<unknown>> = Awaited<ReturnType<T>>;

const AUTH_CHANNEL_NAME = 'storyflo-auth-sync';

type OAuthProvider = 'google' | 'github';

type AuthChannelMessage =
  | {
      type: 'auth-event';
      event: AuthChangeEvent;
      hasSession: boolean;
      source: string;
      timestamp: number;
    }
  | {
      type: 'sync-request';
      source: string;
      timestamp: number;
    }
  | {
      type: 'sync-response';
      hasSession: boolean;
      source: string;
      timestamp: number;
    };

const createTabId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `tab-${Math.random().toString(36).slice(2)}`;
};

const canUseBroadcastChannel = (): boolean => {
  return typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined';
};

export const useAuthStore = defineStore('auth', () => {
  // ============================================================================
  // STATE
  // ============================================================================
  
  const user = ref<User | null>(null);
  const session = ref<Session | null>(null);
  const profile = ref<Profile | null>(null);
  const loading = ref(true);
  const error = ref<string | null>(null);
  const initializing = ref(true);
  const lastPasswordUpdateAt = ref<number>(0);
  const lastAuthEvent = ref<AuthChangeEvent | null>(null);
  const isOnline = ref<boolean>(typeof navigator === 'undefined' ? true : navigator.onLine);
  const toastStore = useToastStore();
  const { isRecoverySession, setRecoveryMode, hydrateRecoveryFlag } = createRecoveryController();
  hydrateRecoveryFlag();

  const OFFLINE_ERROR_MESSAGE = 'No internet connection detected. Reconnect and try again.';
  const getTimestamp = (): number =>
    typeof performance !== 'undefined' ? performance.now() : Date.now();

  const buildErrorMeta = (err: unknown): Record<string, unknown> => {
    if (err instanceof Error) {
      return {
        name: err.name,
        message: err.message,
        stack: err.stack
      };
    }

    if (typeof err === 'object' && err !== null) {
      try {
        return { detail: JSON.stringify(err) };
      } catch {
        return { detail: String(err) };
      }
    }

    return { detail: err };
  };

  const trackEvent = (event: string, meta?: Record<string, unknown>): void => {
    analyticsService.track(event, meta);
  };

  const notifyError = (
    scope: string,
    category: AuthErrorCategory,
    fallbackMessage: string,
    err?: unknown
  ): string => {
    const resolvedMessage = getUserFacingMessage(category, fallbackMessage);
    const meta = err ? { ...buildErrorMeta(err), scope, category } : { scope, category };
    structuredLogger.error('[auth]', { message: resolvedMessage, ...meta });
    trackEvent('auth_error', meta);
    toastStore.addToast({ message: resolvedMessage, variant: 'error' });
    return resolvedMessage;
  };

  const notifySuccess = (
    scope: string,
    message: string,
    meta?: Record<string, unknown>,
    event?: string
  ): void => {
    structuredLogger.info('[auth]', { message, scope, ...meta });
    if (event) {
      trackEvent(event, { scope, ...meta });
    }
    toastStore.addToast({ message, variant: 'success' });
  };

  const guardOffline = (scope: string, shouldNotifyUser: boolean): AuthError | null => {
    if (isOnline.value) {
      return null;
    }

    const offlineError = new Error(OFFLINE_ERROR_MESSAGE);

    if (shouldNotifyUser) {
      const message = notifyError(scope, AuthErrorCategory.Offline, OFFLINE_ERROR_MESSAGE, offlineError);
      error.value = message;
      return normalizeAuthError(offlineError, message);
    }

    structuredLogger.warn('[auth] offline-short-circuit', { scope });
    return normalizeAuthError(offlineError, OFFLINE_ERROR_MESSAGE);
  };

  const updateOnlineStatus = (nextStatus: boolean, reason: string): void => {
    if (isOnline.value === nextStatus) {
      return;
    }

    isOnline.value = nextStatus;
    const statusLabel = nextStatus ? 'online' : 'offline';
    structuredLogger.info('[auth] network-status', { status: statusLabel, reason });
    trackEvent(nextStatus ? 'auth_network_online' : 'auth_network_offline', { reason });

    if (!nextStatus) {
      cancelProactiveRefresh('offline');
      return;
    }

    scheduleProactiveRefresh(session.value, isRecoverySession.value, 'network-resume');
    void syncSessionFromSupabase('remote');
  };

  const setupNetworkListeners = (): void => {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('online', () => {
      updateOnlineStatus(true, 'browser');
    });

    window.addEventListener('offline', () => {
      updateOnlineStatus(false, 'browser');
    });
  };

  let refreshTimerId: ReturnType<typeof setTimeout> | null = null;

  const cancelProactiveRefresh = (reason: string): void => {
    if (!refreshTimerId) {
      return;
    }

    clearTimeout(refreshTimerId);
    refreshTimerId = null;
    structuredLogger.info('[auth] refresh-timer:cleared', { reason });
  };

  const triggerProactiveRefresh = (origin: string): void => {
    const offlineBlock = guardOffline('refresh-session', false);

    if (offlineBlock) {
      structuredLogger.warn('[auth] refresh-timer:offline', { origin });
      return;
    }

    trackEvent('auth_refresh_triggered', { origin });
    void refreshSession();
  };

  const scheduleProactiveRefresh = (
    activeSession: Session | null,
    recoveryActive: boolean,
    origin: string
  ): void => {
    cancelProactiveRefresh('reschedule');

    if (!activeSession) {
      structuredLogger.info('[auth] refresh-timer:skipped', { reason: 'no-session', origin });
      return;
    }

    if (recoveryActive) {
      structuredLogger.info('[auth] refresh-timer:skipped', { reason: 'recovery-mode', origin });
      return;
    }

    if (!activeSession.expires_at) {
      structuredLogger.warn('[auth] refresh-timer:missing-expiry', { origin });
      return;
    }

    const refreshDelay = activeSession.expires_at * 1000 - Date.now() - TOKEN_REFRESH_THRESHOLD_MS;

    if (refreshDelay <= 0) {
      structuredLogger.info('[auth] refresh-timer:immediate', { origin });
      triggerProactiveRefresh('threshold-passed');
      return;
    }

    refreshTimerId = setTimeout(() => {
      refreshTimerId = null;
      triggerProactiveRefresh('scheduled');
    }, refreshDelay);

    trackEvent('auth_refresh_scheduled', { refreshDelayMs: refreshDelay, origin });
  };

const isAuthError = (err: unknown): err is AuthError => {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    ('status' in err || 'name' in err || 'code' in err)
  );
};

const normalizeAuthError = (err: unknown, fallbackMessage: string): AuthError => {
  if (isAuthError(err)) {
    return err;
  }
  return { message: fallbackMessage } as AuthError;
};

  // ============================================================================
  // COMPUTED
  // ============================================================================
  
  const isAuthenticated = computed(() => !!user.value && !!session.value && !isRecoverySession.value);
  
  const userEmail = computed(() => user.value?.email || '');
  
  const fullName = computed(() => profile.value?.full_name || '');
  
  const userInitials = computed(() => {
    if (profile.value?.full_name) {
      const names = profile.value.full_name.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    if (user.value?.email) {
      return user.value.email[0].toUpperCase();
    }
    return 'U';
  });
  
  const avatarUrl = computed(() => profile.value?.avatar_url || null);

const tabId = createTabId();
const authChannel = canUseBroadcastChannel() ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;
let channelInitialized = false;
let authListenerAttached = false;
let initializationPromise: Promise<void> | null = null;
let syncRequestTimer: ReturnType<typeof setTimeout> | null = null;
const SYNC_REQUEST_MIN_DELAY_MS = 150;
const SYNC_REQUEST_MAX_DELAY_MS = 600;
setupAuthChannel();
setupNetworkListeners();

  // ============================================================================
  // ACTIONS
  // ============================================================================
  
  /**
   * Initialize auth state
   * - Get current session
   * - Listen for auth changes
   * - Load user profile if authenticated
   */
  async function initialize(): Promise<void> {
    if (initializationPromise) {
      return initializationPromise;
    }

    initializationPromise = (async () => {
      loading.value = true;
      error.value = null;

      try {
        const { data, error: sessionError } = await withTimeout<SupabasePromiseResult<typeof supabase.auth.getSession>>(
          () => supabase.auth.getSession(),
          DEFAULT_TIMEOUT_MS,
          'Get session'
        );

        if (sessionError) {
          throw sessionError;
        }

        await applySession(data.session, { broadcast: false, event: 'INITIAL_SESSION' });

        if (!authListenerAttached) {
          supabase.auth.onAuthStateChange(async (event, newSession) => {
          await applySession(newSession, { broadcast: false, event });
            broadcastAuthEvent(event);
          });
          authListenerAttached = true;
        }
      } catch (err) {
        const fallbackMessage =
          err instanceof Error ? err.message : 'Failed to initialize authentication';
        const category = categorizeAuthError(err, AuthErrorCategory.Network);
        const userMessage = notifyError('initialize', category, fallbackMessage, err);
        error.value = userMessage;
      } finally {
        loading.value = false;
        initializing.value = false;
      }
    })();

    return initializationPromise;
  }

  /**
   * Sign up with email and password
   */
  async function signUp(
    email: string,
    password: string,
    fullName: string
  ): Promise<{ user: User | null; error: AuthError | null }> {
    loading.value = true;
    error.value = null;

    const offlineError = guardOffline('signup', true);

    if (offlineError) {
      loading.value = false;
      return { user: null, error: offlineError };
    }

    try {
      const { data, error: signUpError } = await withTimeout<SupabasePromiseResult<typeof supabase.auth.signUp>>(
        () =>
          supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
            },
            emailRedirectTo: `${window.location.origin}/auth/callback`
            },
          }),
        DEFAULT_TIMEOUT_MS,
        'Sign up'
      );

      if (signUpError) {
        throw signUpError;
      }

      // Note: User may need to verify email before they can sign in
      // Supabase will send verification email automatically
      
      trackEvent('auth_signup_success', {
        method: 'email',
        userId: data.user?.id ?? null,
        email
      });
      return { user: data.user, error: null };

    } catch (err) {
      const fallbackMessage = err instanceof Error ? err.message : 'Signup failed';
      const category = categorizeAuthError(err, AuthErrorCategory.Network);
      const userMessage = notifyError('signup', category, fallbackMessage, err);
      error.value = userMessage;
      return { user: null, error: normalizeAuthError(err, userMessage) };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Sign in with email and password
   */
  async function signIn(
    email: string,
    password: string
  ): Promise<{ session: Session | null; error: AuthError | null }> {
    loading.value = true;
    error.value = null;

    const offlineError = guardOffline('signin', true);

    if (offlineError) {
      loading.value = false;
      return { session: null, error: offlineError };
    }

    try {
      const { data, error: signInError } = await withTimeout<
        SupabasePromiseResult<typeof supabase.auth.signInWithPassword>
      >(
        () =>
          supabase.auth.signInWithPassword({
            email,
            password,
          }),
        DEFAULT_TIMEOUT_MS,
        'Sign in'
      );

      if (signInError) {
        throw signInError;
      }

      trackEvent('auth_signin_success', {
        method: 'password',
        userId: data.user?.id ?? null,
        email
      });

      return { session: data.session, error: null };

    } catch (err) {
      const fallbackMessage = err instanceof Error ? err.message : 'Login failed';
      const category = categorizeAuthError(err, AuthErrorCategory.Credentials);
      const userMessage = notifyError('signin', category, fallbackMessage, err);
      error.value = userMessage;
      return { session: null, error: normalizeAuthError(err, userMessage) };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Sign in with Google OAuth
   */
  async function signInWithGoogle(redirectPath?: string): Promise<{ error: AuthError | null }> {
    return signInWithOAuthProvider('google', redirectPath);
  }

  /**
   * Sign in with GitHub OAuth
   */
  async function signInWithGitHub(redirectPath?: string): Promise<{ error: AuthError | null }> {
    return signInWithOAuthProvider('github', redirectPath);
  }

  /**
   * Sign out
   */
  async function signOut(): Promise<{ error: AuthError | null }> {
    loading.value = true;
    error.value = null;

    const offlineError = guardOffline('signout', true);

    if (offlineError) {
      loading.value = false;
      return { error: offlineError };
    }

    try {
      const signedOutUserId = user.value?.id;
      const { error: signOutError } = await withTimeout<SupabasePromiseResult<typeof supabase.auth.signOut>>(
        () => supabase.auth.signOut(),
        DEFAULT_TIMEOUT_MS,
        'Sign out'
      );

      if (signOutError) {
        throw signOutError;
      }

      await applySession(null, { broadcast: false, event: 'SIGNED_OUT' });

      notifySuccess(
        'signout',
        'Signed out successfully.',
        { userId: signedOutUserId ?? null },
        'auth_signout_success'
      );
      return { error: null };
    } catch (err) {
      const fallbackMessage = err instanceof Error ? err.message : 'Sign out failed';
      const category = categorizeAuthError(err, AuthErrorCategory.Network);
      const userMessage = notifyError('signout', category, fallbackMessage, err);
      error.value = userMessage;
      return { error: normalizeAuthError(err, userMessage) };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Reset password - send reset email
   */
  async function resetPassword(email: string): Promise<{ error: AuthError | null }> {
    loading.value = true;
    error.value = null;

    const offlineError = guardOffline('reset-password', true);

    if (offlineError) {
      loading.value = false;
      return { error: offlineError };
    }

    try {
      const { error: resetError } = await withTimeout<
        SupabasePromiseResult<typeof supabase.auth.resetPasswordForEmail>
      >(
        () =>
          supabase.auth.resetPasswordForEmail(email, {
            redirectTo: buildCallbackUrl(undefined, 'recovery')
          }),
        DEFAULT_TIMEOUT_MS,
        'Request password reset'
      );

      if (resetError) {
        throw resetError;
      }

      return { error: null };

    } catch (err) {
      const fallbackMessage = err instanceof Error ? err.message : 'Password reset failed';
      const category = categorizeAuthError(err, AuthErrorCategory.Network);
      const userMessage = notifyError('reset-password', category, fallbackMessage, err);
      error.value = userMessage;
      return { error: normalizeAuthError(err, userMessage) };
    } finally {
      loading.value = false;
    }
  }

  const logAuthStep = (step: string, meta: Record<string, unknown> = {}): void => {
    structuredLogger.info('[auth-store]', { step, ...meta });
  };

  /**
   * Update password (must be authenticated)
   */
  async function updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
    loading.value = true;
    error.value = null;

    try {
      if (!isRecoverySession.value) {
        throw new Error('Password reset session not found. Please request a new link.');
      }

      const now = Date.now();
      const PASSWORD_UPDATE_COOLDOWN_MS = 5000;

      if (now - lastPasswordUpdateAt.value < PASSWORD_UPDATE_COOLDOWN_MS) {
        throw new Error('Please wait a moment before updating your password again.');
      }

      const offlineError = guardOffline('update-password', true);

      if (offlineError) {
        loading.value = false;
        return { error: offlineError };
      }

      logAuthStep('update-password:start', {
        isRecoverySession: isRecoverySession.value,
        hasSession: Boolean(session.value)
      });

      const { error: updateError } = await withTimeout<
        SupabasePromiseResult<typeof supabase.auth.updateUser>
      >(
        () =>
          supabase.auth.updateUser({
            password: newPassword,
          }),
        DEFAULT_TIMEOUT_MS,
        'Update password'
      );

      if (updateError) {
        throw updateError;
      }

      lastPasswordUpdateAt.value = now;
      setRecoveryMode(false);
      trackEvent('auth_recovery_completed', { userId: user.value?.id ?? null });
      logAuthStep('update-password:success', {
        hasSession: Boolean(session.value),
        isRecoverySession: isRecoverySession.value
      });

      return { error: null };
    } catch (err) {
      const fallbackMessage = err instanceof Error ? err.message : 'Password update failed';
      const category = categorizeAuthError(err, AuthErrorCategory.Recovery);
      const userMessage = notifyError('update-password', category, fallbackMessage, err);
      error.value = userMessage;
      logAuthStep('update-password:error', { message: userMessage });
      return { error: normalizeAuthError(err, userMessage) };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Load user profile from database
   */
  async function loadProfile(): Promise<void> {
    if (!user.value) return;

    const userId = user.value.id;
    const offlineBlock = guardOffline('load-profile', false);

    if (offlineBlock) {
      return;
    }

    const startTime = getTimestamp();

    try {
      const response = await withRetry<PostgrestSingleResponse<Profile>>(
        () =>
          withTimeout<PostgrestSingleResponse<Profile>>(
            () =>
              supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single(),
            PROFILE_QUERY_TIMEOUT_MS,
            'Load profile'
          ),
        { maxAttempts: 2 }
      );

      const { data, error: profileError } = response;

      if (profileError) {
        const message = profileError.message?.toLowerCase() ?? '';
        const isMissingProfile =
          profileError.code === 'PGRST116' ||
          message.includes('row not found') ||
          message.includes('no rows');

        if (isMissingProfile) {
          structuredLogger.info('[auth] profile-missing', {
            userId,
            message: profileError.message,
          });
          return;
        }

        throw profileError;
      }

      profile.value = data;
      const durationMs = Math.round(getTimestamp() - startTime);
      structuredLogger.info('[auth] load-profile:duration', {
        durationMs,
        userId,
        hasProfile: Boolean(data)
      });
      trackEvent('auth_profile_load_latency', {
        durationMs,
        userId,
        hasProfile: Boolean(data)
      });
    } catch (err) {
      const durationMs = Math.round(getTimestamp() - startTime);
      structuredLogger.warn('[auth] load-profile', {
        ...buildErrorMeta(err),
        durationMs,
        userId
      });
      trackEvent('auth_profile_load_latency', {
        durationMs,
        userId,
        hasProfile: false,
        errored: true
      });
    }
  }

  /**
   * Update user profile
   */
  async function updateProfile(updates: Partial<Profile>): Promise<{ error: Error | null }> {
    const currentUser = user.value;
    if (!currentUser) {
      const err = new Error('Not authenticated');
      error.value = err.message;
      return { error: err };
    }

    loading.value = true;
    error.value = null;

    const offlineError = guardOffline('update-profile', true);

    if (offlineError) {
      loading.value = false;
      return { error: new Error(offlineError.message) };
    }

    try {
      const { error: updateError } = await withTimeout<PostgrestSingleResponse<Profile>>(
        () =>
          supabase
            .from('profiles')
            .update(updates)
            .eq('id', currentUser.id),
        DEFAULT_TIMEOUT_MS,
        'Update profile'
      );

      if (updateError) {
        throw updateError;
      }

      // Reload profile to get updated data
      await loadProfile();

      return { error: null };

    } catch (err) {
      const fallbackMessage = err instanceof Error ? err.message : 'Profile update failed';
      const category = categorizeAuthError(err, AuthErrorCategory.Network);
      const userMessage = notifyError('update-profile', category, fallbackMessage, err);
      error.value = userMessage;
      return { error: err instanceof Error ? err : new Error(userMessage) };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Refresh session manually
   */
  async function refreshSession(): Promise<{ session: Session | null; error: AuthError | null }> {
    const offlineError = guardOffline('refresh-session', false);

    if (offlineError) {
      return { session: null, error: offlineError };
    }

    try {
      const { data, error: refreshError } = await withTimeout<
        SupabasePromiseResult<typeof supabase.auth.refreshSession>
      >(
        () => supabase.auth.refreshSession(),
        DEFAULT_TIMEOUT_MS,
        'Refresh session'
      );

      if (refreshError) {
        throw refreshError;
      }

      await applySession(data.session, { broadcast: false, event: 'TOKEN_REFRESHED' });

      return { session: data.session, error: null };

    } catch (err) {
      structuredLogger.warn('[auth] refresh-session', buildErrorMeta(err));
      return { session: null, error: normalizeAuthError(err, 'Failed to refresh session') };
    }
  }

  /**
   * Clear error message
   */
  function clearError(): void {
    error.value = null;
  }

  async function signInWithOAuthProvider(
    provider: OAuthProvider,
    redirectPath?: string
  ): Promise<{ error: AuthError | null }> {
    loading.value = true;
    error.value = null;

    const offlineError = guardOffline(`oauth:${provider}`, true);

    if (offlineError) {
      loading.value = false;
      return { error: offlineError };
    }

    try {
      const callbackUrl = buildCallbackUrl(redirectPath, 'default');
      const { error: oauthError } = await withTimeout<
        SupabasePromiseResult<typeof supabase.auth.signInWithOAuth>
      >(
        () =>
          supabase.auth.signInWithOAuth({
            provider,
            options: {
              redirectTo: callbackUrl,
            },
          }),
        DEFAULT_TIMEOUT_MS,
        `OAuth sign in (${provider})`
      );

      if (oauthError) {
        throw oauthError;
      }

      return { error: null };
    } catch (err) {
      const fallbackMessage =
        err instanceof Error ? err.message : `OAuth sign in with ${provider} failed`;
      const category = categorizeAuthError(err, AuthErrorCategory.Network);
      const userMessage = notifyError(`oauth:${provider}`, category, fallbackMessage, err);
      error.value = userMessage;
      return { error: normalizeAuthError(err, userMessage) };
    } finally {
      loading.value = false;
    }
  }

  async function syncSessionFromSupabase(origin: 'remote' | 'local'): Promise<void> {
    const offlineBlock = guardOffline(`sync-session:${origin}`, false);

    if (offlineBlock) {
      return;
    }

    try {
      const { data, error: sessionError } = await withTimeout<SupabasePromiseResult<typeof supabase.auth.getSession>>(
        () => supabase.auth.getSession(),
        DEFAULT_TIMEOUT_MS,
        'Sync session'
      );

      if (sessionError) {
        throw sessionError;
      }

      await applySession(data.session, { broadcast: false });
    } catch (err) {
      structuredLogger.warn('[auth] sync-session', {
        origin,
        ...buildErrorMeta(err)
      });
    }
  }

  function broadcastAuthEvent(event: AuthChangeEvent): void {
    if (!authChannel) {
      return;
    }

    const message: AuthChannelMessage = {
      type: 'auth-event',
      event,
      hasSession: isAuthenticated.value,
      source: tabId,
      timestamp: Date.now(),
    };

    authChannel.postMessage(message);
  }

  function broadcastSyncResponse(): void {
    if (!authChannel) {
      return;
    }

    const response: AuthChannelMessage = {
      type: 'sync-response',
      hasSession: isAuthenticated.value,
      source: tabId,
      timestamp: Date.now(),
    };

    authChannel.postMessage(response);
  }

  async function handleChannelMessage(message?: AuthChannelMessage): Promise<void> {
    if (!message || message.source === tabId) {
      return;
    }

    if (message.type === 'auth-event') {
      const offlineBlock = guardOffline('auth-channel:auth-event', false);
      if (!offlineBlock) {
        await syncSessionFromSupabase('remote');
      }
    } else if (message.type === 'sync-request') {
      broadcastSyncResponse();
    } else if (message.type === 'sync-response') {
      if (!isAuthenticated.value && message.hasSession) {
        const offlineBlock = guardOffline('auth-channel:sync-response', false);
        if (!offlineBlock) {
          await syncSessionFromSupabase('remote');
        }
      }
    }
  }

  function scheduleSyncRequest(reason: string): void {
    if (!authChannel) {
      return;
    }

    if (syncRequestTimer) {
      clearTimeout(syncRequestTimer);
    }

    const jitterRange = SYNC_REQUEST_MAX_DELAY_MS - SYNC_REQUEST_MIN_DELAY_MS;
    const delay =
      SYNC_REQUEST_MIN_DELAY_MS + Math.floor(Math.random() * (jitterRange + 1));

    syncRequestTimer = setTimeout(() => {
      const message: AuthChannelMessage = {
        type: 'sync-request',
        source: tabId,
        timestamp: Date.now()
      };

      authChannel.postMessage(message);
      structuredLogger.info('[auth] auth-channel:sync-request', {
        reason,
        delayMs: delay
      });
      syncRequestTimer = null;
    }, delay);
  }

  function setupAuthChannel(): void {
    if (!authChannel || channelInitialized) {
      return;
    }

    authChannel.addEventListener('message', (event) => {
      const incoming = event.data as AuthChannelMessage | undefined;
      Promise.resolve(handleChannelMessage(incoming)).catch((err) => {
        structuredLogger.warn('[auth] auth-channel:listener-error', buildErrorMeta(err));
      });
    });

    authChannel.addEventListener('messageerror', (event) => {
      structuredLogger.warn('[auth] auth-channel:messageerror', {
        data: event.data
      });
    });

    channelInitialized = true;

    scheduleSyncRequest('init');
  }

  // ============================================================================
  // RETURN PUBLIC API
  // ============================================================================
  
  async function applySession(
    newSession: Session | null,
    options: { broadcast?: boolean; event?: AuthChangeEvent } = {}
  ): Promise<void> {
    session.value = newSession;
    user.value = newSession?.user ?? null;
    const authEvent = options.event;

    if (authEvent) {
      lastAuthEvent.value = authEvent;
    }

    logAuthStep('apply-session', {
      event: authEvent ?? 'manual',
      hasSession: Boolean(newSession),
      isRecoverySession: isRecoverySession.value
    });

    if (authEvent === 'INITIAL_SESSION' && typeof window !== 'undefined') {
      const hash = window.location.hash;
      if (hash.includes('type=recovery')) {
        setRecoveryMode(true);
        trackEvent('auth_recovery_mode_entered', {
          userId: newSession?.user?.id ?? null,
          source: 'initial_session_hash'
        });
        logAuthStep('recovery-mode:entered-from-hash', { event: authEvent });
      }
    }

    const shouldEnterRecovery = authEvent === 'PASSWORD_RECOVERY';
    const shouldExitRecovery =
      authEvent === 'SIGNED_IN' ||
      authEvent === 'SIGNED_OUT' ||
      (authEvent === 'USER_UPDATED' && isRecoverySession.value);

    if (shouldEnterRecovery) {
      setRecoveryMode(true);
      trackEvent('auth_recovery_mode_entered', { userId: newSession?.user?.id ?? null });
      logAuthStep('recovery-mode:entered', { event: authEvent });
    } else if (shouldExitRecovery) {
      const wasRecovery = isRecoverySession.value;
      setRecoveryMode(false);

      if (wasRecovery && authEvent === 'USER_UPDATED') {
        trackEvent('auth_recovery_completed', { userId: newSession?.user?.id ?? null });
        logAuthStep('recovery-mode:completed', { event: authEvent });
      } else {
        logAuthStep('recovery-mode:cleared', { event: authEvent });
      }
    } else if (
      authEvent &&
      !['INITIAL_SESSION', 'TOKEN_REFRESHED', 'USER_UPDATED', 'SIGNED_IN', 'SIGNED_OUT', 'PASSWORD_RECOVERY'].includes(
        authEvent
      )
    ) {
      structuredLogger.warn('[auth] Unhandled auth event', { event: authEvent });
    }

    if (newSession?.user) {
      void loadProfile().catch((err) => {
        structuredLogger.warn('[auth] load-profile:deferred-error', buildErrorMeta(err));
      });
    } else {
      profile.value = null;
    }

    if (newSession) {
      scheduleProactiveRefresh(newSession, isRecoverySession.value, authEvent ?? 'manual');
    } else {
      cancelProactiveRefresh('no-session');
    }
  }

  async function resendVerificationEmail(email: string): Promise<{ error: AuthError | null }> {
    loading.value = true;
    error.value = null;

    const offlineError = guardOffline('resend-verification', true);

    if (offlineError) {
      loading.value = false;
      return { error: offlineError };
    }

    try {
      const { error: resendError } = await withTimeout<SupabasePromiseResult<typeof supabase.auth.resend>>(
        () =>
          supabase.auth.resend({
            type: 'signup',
            email,
            options: {
              emailRedirectTo: `${window.location.origin}/auth/callback`
            }
          }),
        DEFAULT_TIMEOUT_MS,
        'Resend verification email'
      );

      if (resendError) {
        throw resendError;
      }

      return { error: null };
    } catch (err) {
      const fallbackMessage =
        err instanceof Error ? err.message : 'Failed to resend verification email';
      const category = categorizeAuthError(err, AuthErrorCategory.Network);
      const userMessage = notifyError('resend-verification', category, fallbackMessage, err);
      error.value = userMessage;
      return { error: normalizeAuthError(err, userMessage) };
    } finally {
      loading.value = false;
    }
  }

  registerDevInspector({
    get isAuthenticated() {
      return isAuthenticated.value;
    },
    get isRecoverySession() {
      return isRecoverySession.value;
    },
    get session() {
      return session.value;
    },
    get user() {
      return user.value;
    },
    get lastAuthEvent() {
      return lastAuthEvent.value;
    },
    dump() {
      return {
        isAuthenticated: isAuthenticated.value,
        isRecoverySession: isRecoverySession.value,
        hasSession: Boolean(session.value),
        lastAuthEvent: lastAuthEvent.value,
      };
    },
  });

  return {
    // State
    user,
    session,
    profile,
    loading,
    initializing,
    isRecoverySession,
    error,
    lastAuthEvent,

    // Computed
    isAuthenticated,
    userEmail,
    fullName,
    userInitials,
    avatarUrl,

    // Actions
    initialize,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithGitHub,
    signOut,
    resetPassword,
    updatePassword,
    loadProfile,
    updateProfile,
    refreshSession,
    clearError,
    resendVerificationEmail,
    applySession,
  };
});

type DevInspector = {
  get isAuthenticated(): boolean;
  get isRecoverySession(): boolean;
  get session(): Session | null;
  get user(): User | null;
  get lastAuthEvent(): AuthChangeEvent | null;
  dump(): Record<string, unknown>;
};

function registerDevInspector(inspector: DevInspector): void {
  if (typeof window === 'undefined') {
    return;
  }

  type DevWindow = typeof window & {
    __PINIA__?: {
      stores?: Record<string, unknown>;
    };
  };
  const devWindow = window as DevWindow;
  devWindow.__PINIA__ = devWindow.__PINIA__ ?? { stores: {} };
  devWindow.__PINIA__.stores = devWindow.__PINIA__.stores ?? {};
  devWindow.__PINIA__.stores.auth = inspector;
}


